import type { CalendarFeed, CalendarItem } from "@ronin/contracts";
import { cn } from "@ronin/ui";
import { Cake } from "lucide-react";
import type { CSSProperties } from "react";
import { isBirthday, itemColor, itemTitle } from "./item-style.js";

interface ItemChipProps {
  item: CalendarItem;
  feeds: ReadonlyMap<string, CalendarFeed>;
  /** Local start time of a timed item ("18:00"). */
  time?: string | null;
  clippedStart?: boolean;
  clippedEnd?: boolean;
  label: string;
  onOpen?: (item: CalendarItem) => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * One-line event bar (month cells, all-day section): coloured stripe and tint, a cake for
 * birthdays, the start time for timed events. Feed and own events open on click; the
 * accessible name carries the full text.
 */
export function ItemChip({
  item,
  feeds,
  time,
  clippedStart,
  clippedEnd,
  label,
  onOpen,
  className,
  style,
}: ItemChipProps) {
  const color = itemColor(item, feeds);
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      style={style}
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.(item);
      }}
      className={cn(
        "flex h-5 min-w-0 items-center gap-1 border-l-3 px-1.5 text-left text-xs text-foreground transition-colors duration-150 hover:brightness-125 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        color.stripe,
        color.tint,
        clippedStart && "border-l-0 pl-2",
        clippedEnd && "pr-0",
        className,
      )}
    >
      {isBirthday(item) && <Cake className="size-3 shrink-0" aria-hidden />}
      {time && <span className="shrink-0 tabular-nums text-muted-foreground">{time}</span>}
      <span className="truncate">{itemTitle(item)}</span>
    </button>
  );
}
